import { useEffect } from 'react';
import { Channel, chatService, GetChannelsParams, SearchChannelsParams } from 'services/chat';
import useSWRInfinite from 'swr/infinite';
import { ChatTabType } from '../types';
import { mapChatTypeToChannelType } from '../utils';

const CHANNELS_LIMIT = 20;

// Key generator for SWR
const getChannelsKey = (
	index: number,
	previousPageData: Channel[] | null,
	activeTab: ChatTabType,
	searchQuery?: string
): GetChannelsParams | SearchChannelsParams | null => {
	const hasSearch = searchQuery?.trim();
	
	// First page, we don't have previousPageData
	if (index === 0 || !previousPageData || previousPageData.length === 0) {
		if (hasSearch) {
			return {
				q: hasSearch,
				type: mapChatTypeToChannelType(activeTab),
				limit: CHANNELS_LIMIT,
				lastChannelId: null,
			} as SearchChannelsParams;
		} else {
			return {
				type: mapChatTypeToChannelType(activeTab),
				limit: CHANNELS_LIMIT,
				lastChannelId: null,
			} as GetChannelsParams;
		}
	}

	// We reached the end
	if (previousPageData.length < CHANNELS_LIMIT) {
		return null;
	}

	// Get the last channel ID for pagination
	const lastChannelId = previousPageData[previousPageData.length - 1]?.id;
	if (hasSearch) {
		return {
			q: hasSearch,
			type: mapChatTypeToChannelType(activeTab),
			limit: CHANNELS_LIMIT,
			lastChannelId,
		} as SearchChannelsParams;
	} else {
		return {
			type: mapChatTypeToChannelType(activeTab),
			limit: CHANNELS_LIMIT,
			lastChannelId,
		} as GetChannelsParams;
	}
};

export function useChannels(activeTab: ChatTabType, searchQuery?: string) {
	const fetcher = async (params: GetChannelsParams | SearchChannelsParams) => {
		try {
			// Check if this is a search request
			if ('q' in params) {
				const result = await chatService.searchChannels(params as SearchChannelsParams);
				return result || [];
			} else {
				const result = await chatService.getChannels(params as GetChannelsParams);
				return result || [];
			}
		} catch (error) {
			console.error('Error fetching channels:', error);
			throw error;
		}
	};

	const {
		data,
		error,
		size,
		setSize,
		isLoading,
		isValidating,
		mutate: mutateChannels,
	} = useSWRInfinite<Channel[]>(
		(index: number, previousPageData: Channel[] | null) =>
			getChannelsKey(index, previousPageData, activeTab, searchQuery),
		fetcher,
		{
			revalidateOnFocus: true,
			revalidateIfStale: true,
			dedupingInterval: 2000,
			shouldRetryOnError: true,
			errorRetryCount: 3,
		}
	);

	useEffect(() => {
		if (error) {
			console.error('SWR channels error:', error);
		}
	}, [data, error]);

	const channels = Array.isArray(data) ? data.filter(Boolean).flat().filter(Boolean) : [];

	const isReachingEnd = !data || !data.length || data[data.length - 1]?.length < CHANNELS_LIMIT;

	const loadMore = () => {
		if (!isLoading && !isReachingEnd) {
			setSize(size + 1);
		}
	};

	return {
		channels,
		error,
		isLoading,
		isValidating,
		loadMore,
		mutateChannels,
		isReachingEnd,
	};
}
